import React from 'react';
import { 
  Code2, 
  Palette, 
  Smartphone, 
  Globe, 
  Zap, 
  ShieldCheck, 
  Clock, 
  HeartHandshake,
  Layout,
  Layers,
  FileJson,
  PenTool
} from 'lucide-react';
import { Project, Skill, Service, Benefit, Testimonial, AiVideo } from './types';

export const SKILLS: Skill[] = [
  { name: 'WordPress Development', percentage: 95, icon: 'SiWordpress' },
  { name: 'AI & Custom Web Coding', percentage: 92, icon: 'Code2' },
  { name: 'AI Video Editing & CapCut', percentage: 90, icon: 'Video' },
  { name: 'HTML5 / CSS3 / JS', percentage: 90, icon: 'SiHtml5' },
  { name: 'Canva & Adobe Illustrator', percentage: 95, icon: 'PenTool' },
  { name: 'Photoshop & Branding', percentage: 88, icon: 'Image' },
  { name: 'SEO & Speed Optimization', percentage: 90, icon: 'SiSpeedtest' },
];

export const SERVICES: Service[] = [
  {
    title: 'WordPress Development',
    description: 'Expert WordPress web developer specializing in custom themes, advanced Elementor Pro integrations, WooCommerce stores, and secure custom setups. Fast, responsive, and easy to manage.',
    iconName: 'Globe',
    tags: ['WordPress', 'Elementor', 'WooCommerce']
  },
  {
    title: 'AI & Custom Coded Websites',
    description: 'Building modern, high-performance websites leveraging cutting-edge AI tools combined with custom clean coding (HTML, CSS, JavaScript, React). Uniquely designed for maximum speed and conversion.',
    iconName: 'Code2',
    tags: ['AI Builder', 'Custom Code', 'React']
  },
  {
    title: 'AI Video Editing & Shorts',
    description: 'Creative AI video editor producing engaging short-form content, Instagram Reels, TikToks, and promotional videos using AI generation, CapCut, automated captions, and sound design.',
    iconName: 'Zap',
    tags: ['AI Video', 'CapCut', 'Reels & Shorts']
  },
  {
    title: 'Logo & Graphic Design',
    description: 'Creating standout brand identities, custom logos, and eye-catching visual assets using Canva, Adobe Illustrator, and Photoshop to make your brand memorable.',
    iconName: 'PenTool',
    tags: ['Branding', 'Canva', 'Illustrator']
  },
  {
    title: 'Website Redesign & Revamp',
    description: 'Transforming outdated or slow websites into modern, high-converting digital portals with responsive mobile layouts and refreshed UI/UX aesthetics.',
    iconName: 'Layers',
    tags: ['Redesign', 'Modern UI', 'Mobile First']
  },
  {
    title: 'Speed & SEO Optimization',
    description: 'Boosting website load speed under 2 seconds and setting up search engine SEO structure to rank higher on Google and improve Lighthouse metrics.',
    iconName: 'Zap',
    tags: ['Lighthouse', 'Speed Boost', 'SEO']
  },
];

export const PROJECTS: Project[] = [
  {
    id: 19,
    title: 'GoDriveify Mobility',
    description: 'A modern vehicle rental and transport booking platform engineered for high performance, featuring automated reservation flows, vehicle fleet showcasing, and seamless mobility solutions.',
    category: 'Development',
    tech: ['React', 'Next.js', 'Tailwind CSS'],
    image: 'https://i.pinimg.com/736x/ca/5b/12/ca5b1205f038143bc578baaa8f07ff29.jpg',
    liveUrl: 'https://godriveify.com/',
  },
  {
    id: 20,
    title: 'Zaviyar Fashion Suite',
    description: 'A luxury boutique and fashion e-commerce showcase application designed with high-end aesthetic presentation, interactive product galleries, and fluid responsive navigation.',
    category: 'Development',
    tech: ['React', 'Next.js', 'E-Commerce'],
    image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=1200',
    liveUrl: 'https://zaviyar-kappa.vercel.app/',
  },
  {
    id: 21,
    title: 'AtlasNex Steel & Industrial',
    description: 'A robust enterprise portal for industrial steel manufacturing and global supply chain logistics, built with high performance specs, interactive service catalog, and inquiry protocols.',
    category: 'Development',
    tech: ['React', 'TypeScript', 'Tailwind CSS'],
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&q=80&w=1200',
    liveUrl: 'https://atlasnex-steel.vercel.app/',
  },
  {
    id: 22,
    title: 'Pearl Trinity Luxury Living',
    description: 'An elegant real estate and luxury residence showcase platform offering immersive property visualizers, modern interior design highlights, and direct client consultation scheduling.',
    category: 'Design',
    tech: ['React', 'UI/UX Design', 'Tailwind CSS'],
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=1200',
    liveUrl: 'https://pearltrinity.vercel.app/',
  },
  {
    id: 23,
    title: 'Furniture Hub Store',
    description: 'A modern interior furniture catalog and e-commerce experience showcasing contemporary home decor with interactive filter search, product details, and sleek UI aesthetics.',
    category: 'Development',
    tech: ['React', 'Tailwind CSS', 'E-Commerce'],
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=1200',
    liveUrl: 'https://furniture-hub-sigma.vercel.app/',
  },
  {
    id: 1,
    title: 'MG Design X',
    description: 'A premium creative design agency platform featuring immersive layouts, advanced typography, and interactive storytelling for global high-end brands. Built with a focus on speed, performance, and pixel-perfect aesthetics.',
    category: 'Design',
    tech: ['React', 'Next.js', 'Framer Motion'],
    image: 'https://i.pinimg.com/736x/e8/ae/b8/e8aeb83ac15b75dac0603e6eba5e4cfc.jpg',
    liveUrl: 'https://mgdesignx.com/',
  },
  {
    id: 2,
    title: 'MSF',
    description: 'A comprehensive professional web deployment featuring modern UI/UX architecture, optimized user experience flows, and high-performance backend integration for seamless digital operations.',
    category: 'Development',
    tech: ['React', 'Next.js', 'Tailwind CSS'],
    image: 'https://i.pinimg.com/736x/51/2a/60/512a606960abff726b62a3ce54cb5206.jpg',
    liveUrl: 'https://dws117jb.org/',
  },
  {
    id: 3,
    title: 'Motral Web Studio',
    description: 'A full-service high-impact digital agency platform showcasing advanced interactive elements, cutting-edge CSS animations, and a focus on mobile-first responsive design principles.',
    category: 'Development',
    tech: ['React', 'Tailwind CSS', 'Framer Motion'],
    image: 'https://i.pinimg.com/736x/af/4d/a5/af4da5dd0b6e4f8b84928fa7d15b41ca.jpg',
    liveUrl: 'https://motral-web-studio.vercel.app/',
  },
  {
    id: 4,
    title: 'TechSkoop Ecosystem',
    description: 'A robust high-performance technology journalism platform optimized for rapid content delivery, real-time engagement, and a focus on accessibility and readability for a global audience.',
    category: 'Development',
    tech: ['React', 'Next.js', 'PostgreSQL'],
    image: 'https://i.pinimg.com/1200x/34/bd/81/34bd81754eeb8a74b932d42deb38354a.jpg',
    liveUrl: 'https://techskoop.vercel.app/',
  },
  {
    id: 5,
    title: 'Bridal Saloon Suite',
    description: 'An exquisitely elegant lifestyle and service booking platform specifically designed for high-end luxury aesthetics, featuring a custom WordPress architecture and seamless booking integration.',
    category: 'Design',
    tech: ['WordPress', 'UI/UX', 'Elementor'],
    image: 'https://i.pinimg.com/1200x/bc/fc/ee/bcfcee3996ce33785a9d03618c61e3c9.jpg',
    liveUrl: 'https://bridal-saloon.vercel.app/',
  },
  {
    id: 6,
    title: 'Pizza Hut Murex',
    description: 'A custom-engineered high-performance food ordering platform with sophisticated real-time order tracking, optimized secure checkout flows, and a focus on intuitive user interface design for maximum conversion.',
    category: 'Development',
    tech: ['React', 'TypeScript', 'E-Commerce'],
    image: 'https://i.pinimg.com/736x/48/56/6c/48566ccd8b479a3ca52f3238ef8621a3.jpg',
    liveUrl: 'https://pizza-hut-murex.vercel.app/',
  },
  {
    id: 7,
    title: 'Motral Particles',
    description: 'An advanced experimental interactive canvas deployment showcasing complex high-frequency particle physics interactions in a web browser environment, utilizing custom physics engines and optimized rendering algorithms.',
    category: 'Development',
    tech: ['Canvas API', 'JavaScript', 'Physics Engine'],
    image: 'https://i.pinimg.com/736x/07/36/8e/07368e033f3bed455db2c19d714570f5.jpg',
    liveUrl: 'https://motralparticles.vercel.app/',
  },
  {
    id: 8,
    title: 'Ahad Visual Identity',
    description: 'A modern professional visual portfolio deployment focusing on minimalist digital architecture, clean code presentation, and a sophisticated showcase of creative branding and technical project implementations.',
    category: 'Development',
    tech: ['React', 'Vite', 'Tailwind'],
    image: 'https://i.pinimg.com/736x/26/5c/43/265c43038b3303cd2d2a87590f8b36c6.jpg',
    liveUrl: 'https://ahadportfolio-inky.vercel.app/',
  },
  {
    id: 9,
    title: 'Ahmad Digital Core',
    description: 'A robust high-performance developer portfolio platform featuring a centralized digital command interface and sophisticated dark-mode optimization. Built with React and TypeScript, this project showcases advanced architectural patterns and a focus on seamless user navigation and technical clarity.',
    category: 'Development',
    tech: ['React', 'TypeScript', 'CSS3'],
    image: 'https://i.pinimg.com/736x/96/4d/36/964d3602afbd96c076f212275c2b5e00.jpg',
    liveUrl: 'https://ahmadprotfolio.vercel.app/',
  },
  {
    id: 10,
    title: 'BizSkoop',
    description: 'A comprehensive modern business directory and high-end corporate networking platform designed specifically for seamless B2B interactions. The platform integrates complex search algorithms, real-time data filtering, and a mobile-first responsive UI to facilitate professional connections globally.',
    category: 'Development',
    tech: ['React', 'Next.js', 'Tailwind'],
    image: 'https://i.pinimg.com/736x/4f/d6/45/4fd64566a496266abfb11dda065cbc2b.jpg',
    liveUrl: 'https://bizskoop.vercel.app/',
  },
  {
    id: 11,
    title: 'Online Talismans',
    description: 'A specialized spiritual services and consultation platform offering personalized digital talismans and interactive expert guidance. Built on a highly customized WordPress infrastructure, this site focuses on speed, security, and a sophisticated visual aesthetic that builds trust with a global audience.',
    category: 'WordPress',
    tech: ['WordPress', 'Elementor', 'SEO'],
    image: 'https://i.pinimg.com/1200x/12/6b/82/126b82b7b5e4ae525891f5d2f8ff5339.jpg',
    liveUrl: 'http://onlinetalismans.vercel.app/',
  },
  {
    id: 12,
    title: 'Islamic Taweez',
    description: 'A dedicated professional portal for authentic Islamic spiritual healing and traditional taweez services. The deployment features a clean, respectful UI, specialized content management through a custom WordPress backend, and extensive SEO optimization to reach individuals seeking spiritual guidance worldwide.',
    category: 'WordPress',
    tech: ['WordPress', 'PHP', 'MySQL'],
    image: 'https://i.pinimg.com/1200x/dc/93/00/dc93002e459999956475ad71c48ee60e.jpg',
    liveUrl: 'http://islamictaweez.vercel.app/',
  },
  {
    id: 13,
    title: 'Rohani Ilaj',
    description: 'A comprehensive global spiritual healing platform providing detailed guidance and interactive rohani ilaj services. Developed with custom WordPress themes and Elementor Pro, the site delivers a high-performance experience with specialized forms and consultation portals tailored for a diverse international clientele.',
    category: 'WordPress',
    tech: ['WordPress', 'UI/UX', 'Elementor'],
    image: 'https://i.pinimg.com/1200x/44/64/fa/4464fa9fa98e2bc02297d3fe688b757a.jpg',
    liveUrl: 'http://rohaniilajsite.vercel.app/',
  },
  {
    id: 14,
    title: 'Quran Sy Ilaj',
    description: 'An educational and service-oriented digital platform focused specifically on Quranic healing and spiritual well-being. This project involved extensive custom WordPress development, tailored SEO strategies for specific niche keywords, and a focus on accessibility to ensure vital spiritual resources are easily reachable.',
    category: 'WordPress',
    tech: ['WordPress', 'Custom Theme', 'SEO'],
    image: 'https://i.pinimg.com/1200x/46/8a/3e/468a3e43584e7cc90ef866741402831d.jpg',
    liveUrl: 'http://quransyilaj.vercel.app/',
  },
  {
    id: 15,
    title: 'Online Istikhara',
    description: 'A robust, secure, and confidential digital platform for requesting and receiving professional online Istikhara services. The architecture prioritizes user privacy through secure PHP data handling and provides an intuitive, high-performance interface built with specialized WordPress integrations.',
    category: 'WordPress',
    tech: ['WordPress', 'Elementor', 'PHP'],
    image: 'https://i.pinimg.com/736x/43/7b/d5/437bd5f55f90964df375b845938cbd03.jpg',
    liveUrl: 'http://onlineistikharasite.vercel.app/',
  },

  {
    id: 17,
    title: 'Free CV Maker',
    description: 'An advanced, high-performance web tool designed for generating professional resumes and CVs with localized speed and efficiency. Developed using React and Tailwind CSS, this application features real-time previewing, dynamic PDF generation, and a focused user experience for rapid professional document creation.',
    category: 'Development',
    tech: ['React', 'Tailwind CSS', 'Vite'],
    image: 'https://i.pinimg.com/736x/7b/88/c3/7b88c3b12aedf4560d668d972ead6d5a.jpg',
    liveUrl: 'https://freecvmaker-motral.vercel.app/',
  },
  {
    id: 18,
    title: 'Elastic One',
    description: 'A cutting-edge, high-frequency digital platform engineered for maximum flexibility and rapid scalability. Featuring a sophisticated UI architecture, optimized performance protocols, and a focus on seamless dynamic user experiences.',
    category: 'Development',
    tech: ['React', 'Next.js', 'Vercel'],
    image: 'https://i.pinimg.com/736x/dc/8c/62/dc8c6217af189cafdd8d79fd86c58b8f.jpg',
    liveUrl: 'https://elastic-one.vercel.app/',
  },
];

export const BENEFITS: Benefit[] = [
  {
    title: 'Fast Delivery',
    description: 'Respecting deadlines is my top priority. I ensure timely delivery without compromising quality.',
    iconName: 'Clock',
  },
  {
    title: 'Clean & Modern',
    description: 'I follow the latest design trends to provide fresh, high-end visual experiences.',
    iconName: 'Palette',
  },
  {
    title: 'Mobile First',
    description: 'Your website will look and function perfectly on every screen, from phones to desktops.',
    iconName: 'Smartphone',
  },
  {
    title: 'Client Focused',
    description: 'I work closely with you to understand your goals and turn your vision into reality.',
    iconName: 'HeartHandshake',
  },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    id: 1,
    name: 'Sarah Jenkins',
    role: 'CEO, TechNova Solutions',
    content: 'Absolutely phenomenal work. The attention to detail, responsiveness, and sheer quality of the final product exceeded our expectations. Our new platform is incredibly fast and looks stunning.',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 2,
    name: 'David Chen',
    role: 'Founder, Elite Designs',
    content: 'Working closely on our redesign project was a breeze. They brought fresh ideas and executed modern UI/UX principles flawlessly. The entire process was professional and timely.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 3,
    name: 'Emily Carter',
    role: 'Marketing Director, Vibe',
    content: 'The custom landing pages delivered have significantly increased our ROI. Truly engineered for high conversion rates. I am impressed by the seamless combination of design and speed optimization.',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 4,
    name: 'Michael Ross',
    role: 'Managing Partner, Ross & Co',
    content: 'Very professional. Transformed our outdated site into a modern, mobile-friendly experience. Highly recommend for any business looking to step up their digital presence.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 5,
    name: 'Amina Al-Mansoor',
    role: 'Founder, Rohani Care Global',
    content: 'Exceeded all expectations in WordPress custom theme development and speed tuning. Our website speed score jumped to 98% on Google PageSpeed! Highly recommended developer.',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 6,
    name: 'Jason Vance',
    role: 'Head of Growth, Motral Media',
    content: 'The AI-generated video shorts and CapCut editing delivered millions of views on Instagram and YouTube Shorts. Creative cuts, auto captions, and super fast turnaround time.',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 7,
    name: 'Elena Rostova',
    role: 'Creative Lead, Studio X',
    content: 'Designed a pristine logo and vector brand identity that perfectly matched our company vision. The Canva graphics and Illustrator assets were delivered ready for print and digital use.',
    avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 8,
    name: 'Tariq Mahmood',
    role: 'Operations Lead, BizSkoop',
    content: 'Building our React and custom coded web platform with integrated AI capabilities was smooth and efficient. Exceptional communication and pristine code quality.',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=150&h=150'
  },
  {
    id: 9,
    name: 'Sophia Williams',
    role: 'E-commerce Owner, Bridal Luxe',
    content: 'Our WooCommerce store redesign was completed in record time. Conversion rates skyrocketed after the new sleek, mobile-optimized UI went live.',
    avatar: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=150&h=150'
  }
];

export const AI_VIDEOS: AiVideo[] = [
  {
    id: 109,
    title: 'Night Safety Tips AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786773165/night_safety_tips.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Night safety tips public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Night Safety', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 108,
    title: 'Rain Car Safety AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786773094/Rain_Car_safety.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Rain car safety public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Rain Safety', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 107,
    title: 'Life Saving Hack AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786772959/life_saving_hack.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Life saving hack public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Life Saving', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 106,
    title: 'Door Opening Safety AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786772859/door_opening.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Door opening safety public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Door Safety', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 105,
    title: 'Book Your Lesson AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786772768/book_your_lesson.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Book your lesson promotional AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Book Lesson', 'AI Commercial', 'Promo', 'CapCut Pro']
  },
  {
    id: 104,
    title: 'Blind Spot Safety AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786772572/blinf_spot.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Blind spot public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Blind Spot', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 103,
    title: 'Bike Safety Tips AI Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786772566/Bike_Safety_tips.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Bike safety tips public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Bike Safety', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 102,
    title: 'Dynamic AI Commercial 0710',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786772156/0710.mp4',
    poster: 'https://i.pinimg.com/736x/a5/e1/e4/a5e1e456f292314d8f44566623e4b98c.jpg',
    description: 'High-impact AI commercial video engineered with custom visual effects, motion graphics, and CapCut editing.',
    tags: ['AI Commercial', 'Promo', 'CapCut Pro']
  },
  {
    id: 101,
    title: 'Godriveify AI Commercial Promo',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786771840/Godriveify.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Godriveify promotional AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Godriveify', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 100,
    title: 'E-Challan AI Commercial Awareness',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786771785/E_challan.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'E-Challan public awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['E-Challan', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 1,
    title: 'Safety Tips AI Commercial Awareness',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786621672/Safety_tips.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'Public safety tips and awareness AI commercial video, engineered with realistic AI visuals, motion graphics, and CapCut audio enhancement.',
    tags: ['Safety Tips', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 2,
    title: 'Dhanola Welfare Society AI Commercial',
    category: 'AI Commercials',
    duration: '0:40',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786621276/Dhanola_welfare_society_Add.mp4',
    poster: 'https://i.pinimg.com/736x/25/32/07/2532073c829fb96808bcb94a3f6063b3.jpg',
    description: 'Dhanola Welfare Society promotional AI commercial ad engineered with realistic AI generation, community impact storytelling, and CapCut sound design.',
    tags: ['Dhanola Welfare', 'AI Commercial', 'Welfare Society', 'CapCut Pro']
  },
  {
    id: 3,
    title: 'iLMA Fashion AI Commercial Reel 2',
    category: 'AI Reels & Shorts',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786620503/imla_2.mp4',
    poster: 'https://i.pinimg.com/736x/33/28/08/3328085f3544bae7c037a09b0979fe15.jpg',
    description: 'iLMA Fashion luxury AI model showcase and commercial reel, crafted with photorealistic AI model generation, kinetic typography, and CapCut sound design.',
    tags: ['iLMA Fashion', 'AI Models', 'CapCut Pro', 'Fashion Reel']
  },
  {
    id: 4,
    title: 'High-Impact AI Video Commercial Ad 1',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786619781/video_ad_1.mp4',
    poster: 'https://i.pinimg.com/736x/f4/2d/ce/f42dce92b62ec73ae2d0c6827b9bfb59.jpg',
    description: 'High-conversion AI video promotional advertisement created with dynamic visual synthesis, AI voiceover, and precision CapCut editing.',
    tags: ['AI Video Ad', 'AI Commercial', 'CapCut Pro', 'Cloudinary']
  },
  {
    id: 5,
    title: 'High-Impact AI Video Commercial Ad 2',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786619918/Vedio_ad_2nd.mp4',
    poster: 'https://i.pinimg.com/736x/f4/2d/ce/f42dce92b62ec73ae2d0c6827b9bfb59.jpg',
    description: 'Dynamic AI video promo advertisement created with realistic visual effects, engaging voiceover, and high-conversion CapCut editing.',
    tags: ['AI Video Ad', 'AI Commercial', 'CapCut Pro', 'Cloudinary']
  },
  {
    id: 6,
    title: 'High-Impact AI Video Commercial Ad 3',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786620031/video_ad_3.mp4',
    poster: 'https://i.pinimg.com/736x/f4/2d/ce/f42dce92b62ec73ae2d0c6827b9bfb59.jpg',
    description: 'High-converting AI video promotional commercial ad engineered with realistic AI generation, motion graphics, and precision CapCut sound design.',
    tags: ['AI Video Ad', 'AI Commercial', 'CapCut Pro', 'Cloudinary']
  },
  {
    id: 7,
    title: 'High-Impact AI Video Commercial Ad 4',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786620237/video_ad_4th.mp4',
    poster: 'https://i.pinimg.com/736x/f4/2d/ce/f42dce92b62ec73ae2d0c6827b9bfb59.jpg',
    description: 'Engaging AI video commercial advertisement engineered with realistic motion synthesis, AI voiceover, and high-converting CapCut video editing.',
    tags: ['AI Video Ad', 'AI Commercial', 'CapCut Pro', 'Cloudinary']
  },
  {
    id: 8,
    title: 'Talbina Organic AI Product Commercial',
    category: 'Product Showcases',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786619096/Talbina_2.mp4',
    poster: 'https://i.pinimg.com/736x/8c/9f/9d/8c9f9ddb18a3f4408b7d2270bef6e58a.jpg',
    description: 'Talbina organic nutrition AI commercial showcase crafted with cinematic food macro photography, AI voiceover narration, and seamless CapCut editing.',
    tags: ['Talbina Organic', 'Product Showcase', 'AI Commercial', 'CapCut Pro']
  },
  {
    id: 9,
    title: 'One-Way Driving AI Safety Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786618854/one_way_driving.mp4',
    poster: 'https://i.pinimg.com/736x/ad/6e/c8/ad6ec8cbc17864d09add0104dc63771f.jpg',
    description: 'One-Way Driving public awareness commercial engineered with realistic AI video synthesis, dynamic vehicle motion graphics, and CapCut sound design.',
    tags: ['One Way Driving', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 10,
    title: 'Safe Driving AI Awareness Commercial',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786618100/safe_Driving.mp4',
    poster: 'https://i.pinimg.com/736x/99/36/b4/9936b455d95ef2cfec5924175cf7c55f.jpg',
    description: 'Safe Driving awareness commercial campaign engineered with realistic AI generation, cinematic motion graphics, and professional CapCut sound editing.',
    tags: ['Safe Driving', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 11,
    title: 'Dr. Hafsa AI Healthcare Commercial',
    category: 'AI Commercials',
    duration: '0:40',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786617815/dr_hafsa.mp4',
    poster: 'https://i.pinimg.com/736x/c8/4e/28/c84e28a2a0ea68d26244b5ee77012a65.jpg',
    description: 'Dr. Hafsa healthcare promotional commercial produced with AI visual synthesis, custom voice synthesis, and high-conversion CapCut editing.',
    tags: ['Dr. Hafsa', 'Healthcare AI', 'AI Commercial', 'CapCut Pro']
  },
  {
    id: 12,
    title: 'Traffic Safety AI Commercial Promo',
    category: 'AI Commercials',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786617646/trafic_safety.mp4',
    poster: 'https://i.pinimg.com/736x/ad/6e/c8/ad6ec8cbc17864d09add0104dc63771f.jpg',
    description: 'High-visibility Traffic Safety public awareness commercial promo engineered with AI generation, realistic motion graphics, and CapCut audio editing.',
    tags: ['Traffic Safety', 'AI Commercial', 'Public Safety', 'CapCut Pro']
  },
  {
    id: 13,
    title: 'iLMA Fashion AI Commercial Reel',
    category: 'AI Reels & Shorts',
    duration: '0:35',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786617031/iLMA_fASHION.mp4',
    poster: 'https://i.pinimg.com/736x/21/c1/91/21c1918a4500a7d529fcec1e26ab7679.jpg',
    description: 'High-fashion AI model showcase and commercial reel for iLMA Fashion, engineered with realistic AI generation, kinetic typography, and CapCut sound editing.',
    tags: ['iLMA Fashion', 'AI Models', 'CapCut Pro', 'Fashion Reel']
  },
  {
    id: 14,
    title: 'Winz Safety AI Commercial Promo',
    category: 'AI Commercials',
    duration: '0:45',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786616427/winz_safety.mp4',
    poster: 'https://i.pinimg.com/736x/21/37/e6/2137e65400b092fcb627e6e742d2a7fd.jpg',
    description: 'High-impact Winz Safety promotional commercial engineered with AI visual synthesis, custom motion graphics, and dynamic CapCut editing.',
    tags: ['Winz Safety', 'AI Commercial', 'CapCut Pro', 'Cloudinary']
  },
  {
    id: 15,
    title: 'DR AG Home Service Promo',
    category: 'Product Showcases',
    duration: '0:40',
    videoUrl: 'https://res.cloudinary.com/rebyfns1/video/upload/v1786616771/DR_AG_home_Service.mp4',
    poster: 'https://i.pinimg.com/736x/f4/2d/ce/f42dce92b62ec73ae2d0c6827b9bfb59.jpg',
    description: 'Professional DR AG Home Service promotional commercial created with AI visual effects, motion graphics, and high-conversion video editing.',
    tags: ['DR AG Home', 'Home Services', 'AI Video', 'CapCut']
  }
];
